/**
 * Exact battle odds via an absorbing Markov chain. No Monte Carlo.
 *
 * ## The `attackerArmies` convention
 *
 * `attackerArmies` is the number of armies **available to lose** — the armies
 * on the attacking territory *minus* the one that must stay behind as a
 * garrison. Published Risk odds tables disagree about this, and that single
 * ambiguity is responsible for most "your odds are wrong" complaints, so the
 * UI must label which convention it is showing.
 *
 * A forward pass over the state space accumulates the probability of reaching
 * each absorbing state, which yields the win probability, the expected losses,
 * *and* the full outcome distribution for a histogram — all in one O(A*D) walk.
 */

import { lossDistribution } from './dice.js';

export interface DiceConfig {
  readonly maxAttackDice: number;
  readonly maxDefendDice: number;
}

export const STANDARD_DICE: DiceConfig = Object.freeze({ maxAttackDice: 3, maxDefendDice: 2 });

export interface BattleOutcome {
  /** Attacker armies still available to lose. 0 means the attack failed. */
  readonly attackerRemaining: number;
  /** Defending armies left. 0 means the territory was taken. */
  readonly defenderRemaining: number;
  readonly probability: number;
}

export interface BattleOdds {
  readonly attackerArmies: number;
  readonly defenderArmies: number;
  readonly attackerWinProbability: number;
  /** Unconditional expectations, across win and loss alike. */
  readonly expectedAttackerLosses: number;
  readonly expectedDefenderLosses: number;
  /** Conditional expectations. `NaN` when the condition is impossible. */
  readonly expectedAttackerSurvivorsGivenWin: number;
  readonly expectedDefenderSurvivorsGivenHold: number;
  /** Every absorbing state with non-zero probability, for a histogram. */
  readonly outcomes: readonly BattleOutcome[];
}

/** A Risk board never approaches this; it exists to catch a bad config. */
const MAX_ARMIES = 500;

const cache = new Map<string, BattleOdds>();

export function computeBattleOdds(
  attackerArmies: number,
  defenderArmies: number,
  dice: DiceConfig = STANDARD_DICE,
): BattleOdds {
  assertCount(attackerArmies, 'attackerArmies');
  assertCount(defenderArmies, 'defenderArmies');

  const key = `${dice.maxAttackDice}:${dice.maxDefendDice}:${attackerArmies}:${defenderArmies}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const computed = solve(attackerArmies, defenderArmies, dice);
  cache.set(key, computed);
  return computed;
}

function solve(attackerArmies: number, defenderArmies: number, dice: DiceConfig): BattleOdds {
  const width = defenderArmies + 1;
  // reach[a * width + d] = probability of ever occupying state (a, d).
  const reach = new Float64Array((attackerArmies + 1) * width);
  reach[attackerArmies * width + defenderArmies] = 1;

  const outcomes: BattleOutcome[] = [];

  // Every transition strictly decreases a + d, so walking a and d downward
  // guarantees each state is finalised before it is read.
  for (let a = attackerArmies; a >= 0; a -= 1) {
    for (let d = defenderArmies; d >= 0; d -= 1) {
      const p = reach[a * width + d]!;
      if (p === 0) continue;

      if (a === 0 || d === 0) {
        outcomes.push({ attackerRemaining: a, defenderRemaining: d, probability: p });
        continue;
      }

      const roll = lossDistribution(
        Math.min(dice.maxAttackDice, a),
        Math.min(dice.maxDefendDice, d),
      );
      for (const outcome of roll.outcomes) {
        if (outcome.numerator === 0) continue;
        const next = (a - outcome.attackerLosses) * width + (d - outcome.defenderLosses);
        reach[next]! += p * (outcome.numerator / roll.denominator);
      }
    }
  }

  let winProbability = 0;
  let attackerRemainingTotal = 0;
  let defenderRemainingTotal = 0;
  let attackerRemainingGivenWin = 0;
  let defenderRemainingGivenHold = 0;

  for (const outcome of outcomes) {
    attackerRemainingTotal += outcome.attackerRemaining * outcome.probability;
    defenderRemainingTotal += outcome.defenderRemaining * outcome.probability;
    if (outcome.defenderRemaining === 0) {
      winProbability += outcome.probability;
      attackerRemainingGivenWin += outcome.attackerRemaining * outcome.probability;
    } else {
      defenderRemainingGivenHold += outcome.defenderRemaining * outcome.probability;
    }
  }
  const holdProbability = 1 - winProbability;

  return {
    attackerArmies,
    defenderArmies,
    attackerWinProbability: winProbability,
    expectedAttackerLosses: attackerArmies - attackerRemainingTotal,
    expectedDefenderLosses: defenderArmies - defenderRemainingTotal,
    expectedAttackerSurvivorsGivenWin:
      winProbability > 0 ? attackerRemainingGivenWin / winProbability : Number.NaN,
    expectedDefenderSurvivorsGivenHold:
      holdProbability > 0 ? defenderRemainingGivenHold / holdProbability : Number.NaN,
    outcomes,
  };
}

/**
 * Odds for a single roll, for when a player deliberately rolls fewer dice than
 * the maximum. The full-battle DP assumes maximum dice every roll, so it is
 * off-model in that case and the UI shows this instead.
 */
export function singleRollOdds(
  attackerDice: number,
  defenderDice: number,
): readonly (LossProbability)[] {
  const roll = lossDistribution(attackerDice, defenderDice);
  return roll.outcomes.map((outcome) => ({
    attackerLosses: outcome.attackerLosses,
    defenderLosses: outcome.defenderLosses,
    probability: outcome.numerator / roll.denominator,
  }));
}

export interface LossProbability {
  readonly attackerLosses: number;
  readonly defenderLosses: number;
  readonly probability: number;
}

function assertCount(value: number, name: string): void {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer, got ${value}`);
  }
  if (value > MAX_ARMIES) {
    throw new RangeError(`${name} exceeds the supported maximum of ${MAX_ARMIES}`);
  }
}
